/**
 * Utility functions to access authentication data from localStorage
 * These functions do not trigger React re-renders
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  ID_TOKEN: 'idToken',
  USER_ROLES: 'userRoles',
  USER: 'user',
  IS_AUTHENTICATED: 'isAuthenticated',
} as const;

/**
 * Get access token from localStorage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Get ID token from localStorage
 */
export const getIdToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
};

/**
 * Get user roles from localStorage
 */
export const getUserRoles = (): string[] => {
  try {
    const roles = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
    return roles ? JSON.parse(roles) : [];
  } catch (error) {
    console.error('Error parsing user roles:', error);
    return [];
  }
};

/**
 * Get user data from localStorage
 */
export const getUser = (): Record<string, unknown> | null => {
  try {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get authenticated user's Auth0 ID (sub claim) from localStorage.
 * This is a non-hook alternative to useAuthUserId for use outside React components.
 *
 * @returns {string} The user's Auth0 ID (sub claim), or empty string if not authenticated
 *
 * @example
 * ```typescript
 * const userId = getUserId();
 * // Returns: "auth0|69019a2365148bd755e83a77" or ""
 * ```
 */
export const getUserId = (): string => {
  const user = getUser();
  return (user?.sub as string) || '';
};

/**
 * Get the Auth0 organization ID (org_id claim) from localStorage.
 * This is set by Auth0 when the user is logged in under an organization.
 *
 * @returns {string} The Auth0 organization ID, or empty string if not available
 *
 * @example
 * ```typescript
 * const orgId = getOrgId();
 * // Returns: "org_LjrNB9i401WPAdtp" or ""
 * ```
 */
export const getOrgId = (): string => {
  const user = getUser();
  return (user?.org_id as string) || '';
};

/**
 * Check if user is authenticated
 */
export const isUserAuthenticated = (): boolean => {
  return localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED) === 'true';
};

/**
 * Clear all authentication and user identity data from localStorage.
 * Reads the userId before removing auth keys so the per-user cache can be cleaned up.
 */
export const clearAuthData = (): void => {
  // Capture userId BEFORE removing the 'user' key
  const userId = getUserId();
  if (userId) {
    localStorage.removeItem(`lupp-user-identity-${userId}`);
  }

  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.IS_AUTHENTICATED);
};

/**
 * Check if user has a specific role
 */
export const hasRole = (role: string): boolean => {
  const roles = getUserRoles();
  return roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (roles: string[]): boolean => {
  const userRoles = getUserRoles();
  return roles.some((role) => userRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (roles: string[]): boolean => {
  const userRoles = getUserRoles();
  return roles.every((role) => userRoles.includes(role));
};
